// @ts-check
// /api/academy/cohorts — CohortsEndpoints (Features/Cohorts).
//
// Reads are behind academy.cohorts.view; every write is behind academy.cohorts.edit.
// Seat numbers (`filled`, `fillPct`, `canAccept`) are computed server-side by
// the seat engine — never derive them from `seats` client-side.

import { get, post, patch, del, withIdempotency, isEmptyPatch } from '../../api';

/**
 * @typedef {import('@academy-api').CohortResponse} CohortResponse
 * @typedef {import('@academy-api').CohortListItemResponse} CohortListItemResponse
 * @typedef {import('@academy-api').CohortCreateRequest} CohortCreateRequest
 * @typedef {import('@academy-api').CohortPatchRequest} CohortPatchRequest
 * @typedef {import('@academy-api').AssignInstructorRequest} AssignInstructorRequest
 * @typedef {import('@academy-api').TransitionCohortRequest} TransitionCohortRequest
 * @typedef {import('@academy-api').LinkCrmAccountRequest} LinkCrmAccountRequest
 * @typedef {import('@academy-api').PageRequest} PageRequest
 * @typedef {import('@academy-api').BatchMode} BatchMode
 */

/**
 * One cohort, with the live seat picture and the CRM account resolved by id.
 *
 * `state` here is camelCase ("draft") — a real enum on the wire. The cockpit
 * register spells the same concept "Draft"; both are correct, from different
 * endpoints. Do not normalise one into the other.
 *
 * @param {string} id
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<CohortResponse>}
 */
export const getCohort = (id, options = {}) => get(`/cohorts/${id}`, options);

/**
 * The list, with the FULL seat rollup per row (one grouped query, then the
 * engine). Thinner than CohortResponse only in `crmAccountName`, which costs a
 * port call per row — fetch the detail for it.
 *
 * `options.market` narrows to one country's cohorts (the global header filter);
 * `q` searches the code and the programme name, `state` is one lifecycle
 * state, and `unstaffed` keeps only cohorts past Draft with no instructor
 * (PLATFORM-UX-AUDIT §4.2).
 *
 * @param {PageRequest} [page]
 * @param {{ market?: string, q?: string, state?: string, unstaffed?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<import('@academy-api').PagedResult<CohortListItemResponse>>}
 */
export const listCohorts = (page, options = {}) => {
    const { market, q, state, unstaffed, ...rest } = options;
    // The register's filters (PLATFORM-UX-AUDIT §4.2): a search over the code
    // and the programme name, one lifecycle state, and "no instructor yet".
    // Each is sent only when set, so the plain list is the request it always was.
    const params = {
        ...(market ? { market } : {}),
        ...(q ? { q } : {}),
        ...(state ? { state } : {}),
        ...(unstaffed ? { unstaffed: true } : {}),
    };
    return get('/cohorts', {
        ...rest,
        page,
        ...(Object.keys(params).length > 0 ? { params } : {}),
    });
};

/**
 * The register's status-card counts: a total, the lifecycle breakdown, and
 * the unstaffed queue's own count — narrowed by the header's country filter
 * only, never by the screen's search/state/unstaffed filters (those would
 * make the cards restate the table). Server-narrowed to an instructor's own
 * cohorts exactly as the list is, for a caller without academy.trainers.view.
 *
 * @param {{ market?: string, signal?: AbortSignal }} [options]
 * @returns {Promise<import('@academy-api').CohortsSummary>}
 */
export const getCohortsSummary = (options = {}) => {
    const { market, ...rest } = options;
    return get('/cohorts/summary', {
        ...rest,
        ...(market ? { params: { market } } : {}),
    });
};

/**
 * Create. academy.cohorts.add, and [Idempotent] on the server.
 *
 * `code` is unique, so a retry that resends the SAME code fails loudly — the
 * dangerous retry is one that regenerated a fresh code client-side and slipped
 * in a duplicate cohort. withIdempotency makes attempt two a replay of attempt
 * one instead.
 *
 * @param {CohortCreateRequest} request
 * @returns {Promise<{ id: string }>}
 */
export const createCohort = (request) =>
    withIdempotency((key) => post('/cohorts', request, { idempotencyKey: key }));

/**
 * Patch. PASS A DIRTY-FIELD DIFF, NEVER FORM STATE — Opt<T> tri-state: an
 * absent key leaves the column alone, an explicit null writes NULL. Build the
 * body with buildPatch(original, current). academy.cohorts.edit.
 *
 * `curriculumVersionId` is not patchable BY DESIGN: re-pinning a live cohort
 * would regrade its learners against a curriculum they were never taught.
 *
 * @param {string} id
 * @param {Partial<CohortPatchRequest>} dirtyFields
 */
export const patchCohort = (id, dirtyFields) => {
    // An empty patch is a round trip that can still fail with stale_write for
    // no gain.
    if (isEmptyPatch(dirtyFields)) return Promise.resolve(null);
    return patch(`/cohorts/${id}`, dirtyFields);
};

/**
 * The same patch applied to many cohorts. academy.cohorts.edit.
 *
 * `mode` is REQUIRED and goes in the QUERY STRING because this is a batch
 * PATCH. atomic -> all or nothing; bestEffort -> 207 with per-item outcomes,
 * and A 207 IS NOT A SUCCESS: check `allSucceeded` before rendering green.
 *
 * @param {string[]} ids
 * @param {Partial<CohortPatchRequest>} dirtyFields
 * @param {BatchMode} mode
 * @returns {Promise<import('@academy-api').BatchResult<string>>}
 */
export const patchManyCohorts = (ids, dirtyFields, mode) =>
    patch('/cohorts', dirtyFields, { params: { ids, mode } });

/**
 * Delete. academy.cohorts.delete. 204 on success; refusals arrive as
 * AcademyApiError — branch on `error.code`, never on the response body.
 * @param {string} id
 */
export const deleteCohort = (id) => del(`/cohorts/${id}`);

/**
 * Assign or replace the instructor. academy.cohorts.edit.
 * @param {string} id
 * @param {AssignInstructorRequest} request
 */
export const assignInstructor = (id, request) =>
    post(`/cohorts/${id}/instructor`, request);

/**
 * Move the cohort through its lifecycle. academy.cohorts.edit.
 * `target` is a camelCase CohortState ("confirmed"), not the register's
 * PascalCase spelling — sending "Confirmed" is a validation error.
 * @param {string} id
 * @param {TransitionCohortRequest} request
 */
export const transitionCohort = (id, request) =>
    post(`/cohorts/${id}/transition`, request);

/**
 * May the caller author THIS cohort's delivery content — the per-row question
 * a screen-permission gate cannot answer, so a screen deciding whether to render an editor
 * asks the server. academy.teaching.view.
 *
 * A `true` only ever arrives as 200. Every refusal is an AcademyApiError whose
 * `code` says which condition failed, and three of them matter here:
 * `forbidden` (not this cohort's instructor), `instructor_delisted` (assigned
 * but delisted — re-certification restores authoring, re-login never does),
 * `cohort_closed` (Completed/Cancelled — the content is read-only for good).
 *
 * @param {string} id
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<import('@academy-api').AuthoringPermissionResponse>}
 */
export const getAuthoringPermission = (id, options = {}) =>
    get(`/cohorts/${id}/authoring-permission`, options);

/**
 * Link to a crm.account BY ID, with the role it plays. There is deliberately
 * no endpoint that accepts an account NAME — the name on CohortResponse is
 * resolved from this id, never re-typed. academy.cohorts.edit.
 * @param {string} id
 * @param {LinkCrmAccountRequest} request
 */
export const linkCrmAccount = (id, request) =>
    post(`/cohorts/${id}/crm-account`, request);
